import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, CircularProgress, Dialog, TextField } from "@material-ui/core";
import { Settings, Image, PlaylistAddCheck } from "@material-ui/icons";
import { getNodesBounds } from "reactflow";
import { toPng } from "html-to-image";
import { saveAs } from "file-saver";
import { Transition } from "../../../../utils/ModalHelpers";
import DelayedRedirect from "../../../../utils/DelayedRedirect";
import AppBarComponent from "../../../Common/Headers/AppBarComponent";
import FlowCanvas from "./components/FlowCanvas";
import FlowSettingsModal from "./components/FlowSettingsModal";
import DiagnosticFlowTranslations from "./components/DiagnosticFlowTranslations";
import { useDiagnosticFlowData } from "./hooks/useDiagnosticFlowData";
import { getDiagnosticFlowTranslations } from "./services/diagnosticFlowsApiService";
import { parseTranslationBlob } from "./utils/translationBlob";
import { TRANSLATION_LANGUAGES } from "./utils/translationConstants";
import { translationPercentByNode } from "./mappers/diagnosticFlowTranslationsMapper";

// Figma: white button, Neutral/N300 border, Blue/B600 label + icon.
const TOOLBAR_BUTTON = {
  color: "#53B3EA",
  borderColor: "#DAE1E6",
  backgroundColor: "#FFFFFF",
  fontWeight: 700,
  height: "40px",
};

export default function DiagnosticFlowsCreation({
  resources,
  flowId,
  accessibleMoboOutlets,
  objectType,
  hasTranslationLicence,
}) {
  const {
    isEdit,
    loading,
    flowName,
    flowSettings,
    nodes,
    edges,
    validation,
    saving,
    redirect,
    hasUnsavedChanges,
    setFlowSettings,
    handleNameChange,
    handleCanvasChange,
    runValidation,
    clearValidation,
    save,
  } = useDiagnosticFlowData({ resources, flowId });

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [translateNodeId, setTranslateNodeId] = useState(null);
  const [translationBlobs, setTranslationBlobs] = useState({});
  const settingsBtnRef = useRef(null);
  const flowInstanceRef = useRef(null);
  const history = useHistory();

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
    if (settingsBtnRef.current) settingsBtnRef.current.blur();
  }, []);

  const refreshTranslationBlobs = useCallback(async () => {
    if (!isEdit) {
      setTranslationBlobs({});
      return;
    }
    const list = await getDiagnosticFlowTranslations(flowId);
    const blobs = {};
    (list || []).forEach(({ language, jsonTranslation }) => {
      blobs[language] = parseTranslationBlob(jsonTranslation);
    });
    setTranslationBlobs(blobs);
  }, [isEdit, flowId]);

  useEffect(() => {
    refreshTranslationBlobs();
  }, [refreshTranslationBlobs]);

  const translationPercents = useMemo(
    () => translationPercentByNode(nodes, translationBlobs, TRANSLATION_LANGUAGES),
    [nodes, translationBlobs]
  );

  const handleDownloadImage = () => {
    const viewport = document.querySelector(".react-flow__viewport");
    const list = flowInstanceRef.current?.getNodes() || nodes || [];
    if (!viewport || !list.length) return;

    // Size the image to the flow at ~100% so a big flow stays legible, capped at MAX_DIMENSION.
    const MARGIN = 40;
    const MAX_DIMENSION = 4096;
    const bounds = getNodesBounds(list);
    const zoom = Math.min(
      1,
      (MAX_DIMENSION - MARGIN * 2) / bounds.width,
      (MAX_DIMENSION - MARGIN * 2) / bounds.height
    );
    const width = Math.round(bounds.width * zoom + MARGIN * 2);
    const height = Math.round(bounds.height * zoom + MARGIN * 2);

    toPng(viewport, {
      backgroundColor: "#ffffff",
      width,
      height,
      pixelRatio: 2,
      style: {
        width: `${width}px`,
        height: `${height}px`,
        transform: `translate(${MARGIN - bounds.x * zoom}px, ${MARGIN - bounds.y * zoom}px) scale(${zoom})`,
      },
    }).then((dataUrl) => saveAs(dataUrl, `${flowName?.trim() || "diagnostic-flow"}.png`));
  };

  if (redirect) {
    return (
      <DelayedRedirect
        to={{ pathname: redirect.pathname, state: redirect.state }}
        delay={800}
      />
    );
  }

  const validateColor = validation
    ? validation.isValid
      ? { borderColor: "#43a047", color: "#43a047" }
      : { borderColor: "#e53935", color: "#e53935" }
    : {};

  return (
    <Dialog fullScreen open TransitionComponent={Transition}>
      <AppBarComponent
        idName="diagnostic-flow"
        title={
          isEdit
            ? resources["EditDiagnosticFlow"]
            : resources["NewDiagnosticFlow"]
        }
        resources={resources}
        handleClose={() => history.push("/diagnosticflows")}
        handleSubmit={save}
        disabled={saving || !hasUnsavedChanges}
      />

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="calc(100vh - 64px)"
        >
          <CircularProgress />
        </Box>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 64px)",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            padding="10px 16px"
            style={{
              gap: "12px",
              borderBottom: "1px solid #e0e0e0",
              background: "#fff",
            }}
          >
            <TextField
              id="diagnostic-flow-name"
              value={flowName}
              onChange={(e) => handleNameChange(e.target.value)}
              variant="outlined"
              size="small"
              style={{ minWidth: "320px" }}
              placeholder={resources["EnterAName"]}
              inputProps={{ maxLength: 256 }}
              error={validation && !validation.isValid && !flowName.trim()}
            />

            <Button
              id="diagnostic-flow-settings-button"
              ref={settingsBtnRef}
              variant="outlined"
              size="small"
              startIcon={<Settings />}
              onClick={() => setSettingsOpen(true)}
              style={TOOLBAR_BUTTON}
            >
              {resources["Settings"]}
            </Button>

            <div style={{ flex: 1 }} />

            <Button
              id="diagnostic-flow-download-image"
              variant="outlined"
              size="small"
              startIcon={<Image />}
              onClick={handleDownloadImage}
              style={TOOLBAR_BUTTON}
            >
              {resources["DownloadImage"]}
            </Button>

            <Button
              id="diagnostic-flow-validate"
              variant="outlined"
              size="small"
              startIcon={<PlaylistAddCheck />}
              onClick={runValidation}
              style={{ ...TOOLBAR_BUTTON, ...validateColor }}
            >
              {resources["ValidateFlow"]}
            </Button>
          </Box>

          <div style={{ flex: 1, minHeight: 0 }}>
            <FlowCanvas
              nodes={nodes}
              edges={edges}
              onChange={handleCanvasChange}
              validation={validation}
              onValidationClose={clearValidation}
              resources={resources}
              onOpenTranslations={isEdit ? setTranslateNodeId : undefined}
              translationPercents={translationPercents}
              onInstanceReady={(instance) => {
                flowInstanceRef.current = instance;
              }}
            />
          </div>
        </div>
      )}

      <FlowSettingsModal
        open={settingsOpen}
        onClose={closeSettings}
        onSave={setFlowSettings}
        value={flowSettings}
        accessibleMoboOutlets={accessibleMoboOutlets}
        objectType={objectType}
        resources={resources}
      />

      <DiagnosticFlowTranslations
        open={!!translateNodeId}
        flowId={flowId}
        filterNodeId={translateNodeId}
        fullScreen={false}
        resources={resources}
        hasTranslationLicence={hasTranslationLicence}
        onClose={() => {
          setTranslateNodeId(null);
          refreshTranslationBlobs();
        }}
      />
    </Dialog>
  );
}
